import { ApiError, NetworkError } from '../../lib/api';
import type { AuthedRequest } from '../auth/AuthProvider';
import { formatBytes } from '../system/media';

/** Mirrors a node of GET /api/v1/platform/media-nodes (ADR-048). The key is never in it. */
export interface MediaNode {
  id: string;
  name: string;
  engine_url: string;
  agent_url: string;
  snap_pump_url: string;
  public_host: string;
  rtsp_port: number;
  replay_port: number;
  webrtc_port: number;
  capacity: number;
  status: 'active' | 'paused' | 'disabled';
  /** Created from the environment (all-in-one installation). */
  local: boolean;
  /** Health: false after 3 checks in a row without an answer (15 s apart). */
  up: boolean;
  usage: { weight: number; equipment: number };
  /** Codes of the node's active warnings (0.60.0); empty outside the list and the node's page. */
  warnings?: NodeWarningCode[];
  created_at: string;
  updated_at: string;
}

export type MediaNodeInput = Partial<
  Pick<
    MediaNode,
    | 'name'
    | 'engine_url'
    | 'agent_url'
    | 'snap_pump_url'
    | 'public_host'
    | 'rtsp_port'
    | 'replay_port'
    | 'webrtc_port'
    | 'capacity'
    | 'status'
  >
>;

/** An equipment (a recorder with its channels, or a camera) on a node. */
export interface Equipment {
  device_id: string;
  tenant_id: string;
  tenant_name: string;
  site_name: string;
  name: string;
  kind: string;
  cameras: number;
  weight: number;
}

/** Why a due failover does not move (ADR-050): the platform tenant, where it is audited, is missing. */
export type FailoverBlock = 'platform_tenant';

/** An unavailable node with equipment, waiting for (or in) its failover (ADR-050). */
export interface PendingFailover {
  node_id: string;
  node_name: string;
  down_since: string;
  /** down_since + 2 min: when its equipment starts moving. */
  moves_at: string;
  equipment: number;
  /** No other node is active and available to receive the equipment. */
  no_target: boolean;
  /**
   * Why the failover, already due and with a target, moves nothing; null (or absent) when
   * nothing blocks it. It is the view of the API instance that answered.
   */
  blocked?: FailoverBlock | null;
}

/** The latest failover that moved equipment. */
export interface FailoverRun {
  /** null once the node was deleted (its name stays). */
  node_id: string | null;
  node_name: string;
  started_at: string;
  finished_at: string;
  outcome: 'done' | 'aborted' | 'no_target';
  moved: number;
  /** Equipment still on the node at the end. */
  left: number;
  /** Equipment moved to each node, by node name. */
  targets: Record<string, number>;
  /** Nodes left above 100% of their capacity. */
  over_capacity: string[];
}

export interface FailoverStatus {
  /** The server's clock when it answered: countdowns use it, not the browser's. */
  now: string;
  pending: PendingFailover[];
  last: FailoverRun | null;
}

export interface MediaNodeList {
  items: MediaNode[];
  failover: FailoverStatus;
  /** Date.now() when the answer arrived, to advance the server's clock. */
  receivedAt: number;
}

/** One move of a rebalance preview, in the order it runs. */
export interface RebalanceMove {
  device_id: string;
  name: string;
  tenant_name: string;
  weight: number;
  from: { id: string; name: string };
  to: { id: string; name: string };
}

/** New equipment goes only to a node that stays within this share of its capacity (ADR-050). */
export const headroomPercent = 70;

/** What blocks a due failover, in pt-BR, for the operator to act on. */
export const failoverBlockLabels: Record<FailoverBlock, string> = {
  platform_tenant:
    'Troca bloqueada: a organização da plataforma não existe ou não pôde ser lida — rode vmsctl bootstrap-tenant -platform ou confira o banco (visto pela instância da API que respondeu)',
};

export const mediaNodesKey = ['platform', 'media-nodes'] as const;
export const equipmentKey = (id: string) => ['platform', 'media-nodes', id, 'equipment'] as const;
export const rebalanceKey = ['platform', 'media-nodes', 'rebalance'] as const;
const path = '/api/v1/platform/media-nodes';

export async function listMediaNodes(req: AuthedRequest): Promise<MediaNodeList> {
  const r = await req<{ items: MediaNode[]; failover?: FailoverStatus }>('GET', path);
  const receivedAt = Date.now();
  return {
    items: r.items,
    failover: r.failover ?? { now: new Date(receivedAt).toISOString(), pending: [], last: null },
    receivedAt,
  };
}

/** Registers a node. The key in the answer is shown once and never kept. */
export const createMediaNode = (req: AuthedRequest, body: MediaNodeInput) =>
  req<{ node: MediaNode; key: string }>('POST', path, body);

export const updateMediaNode = (req: AuthedRequest, id: string, body: MediaNodeInput) =>
  req<MediaNode>('PATCH', `${path}/${encodeURIComponent(id)}`, body);

/** Issues a new key; the old one stops working at once. Shown once and never kept. */
export const rotateMediaNodeKey = (req: AuthedRequest, id: string) =>
  req<{ key: string }>('POST', `${path}/${encodeURIComponent(id)}/key`);

export async function listEquipment(req: AuthedRequest, id: string): Promise<Equipment[]> {
  return (await req<{ items: Equipment[] }>('GET', `${path}/${encodeURIComponent(id)}/equipment`))
    .items;
}

/** Moves an equipment (recorder with its channels, or camera) onto nodeId. */
export const moveEquipment = (req: AuthedRequest, nodeId: string, deviceId: string) =>
  req<null>('POST', `${path}/${encodeURIComponent(nodeId)}/equipment`, { device_id: deviceId });

export async function previewRebalance(req: AuthedRequest): Promise<RebalanceMove[]> {
  return (await req<{ moves: RebalanceMove[] }>('GET', `${path}/rebalance`)).moves;
}

/** Runs exactly the previewed moves; 409 rebalance_changed when the state moved on. */
export const runRebalance = (req: AuthedRequest, moves: RebalanceMove[]) =>
  req<{ moved: number }>('POST', `${path}/rebalance`, {
    moves: moves.map((m) => ({ device_id: m.device_id, from: m.from.id, to: m.to.id })),
  });

/** The moves grouped by route, in the order of each route's first move. */
export function rebalanceRoutes(
  moves: RebalanceMove[],
): { from: string; to: string; count: number }[] {
  const out: { from: string; to: string; count: number }[] = [];
  for (const m of moves) {
    const r = out.find((x) => x.from === m.from.name && x.to === m.to.name);
    if (r) r.count += 1;
    else out.push({ from: m.from.name, to: m.to.name, count: 1 });
  }
  return out;
}

/** A duration as m:ss (h:mm:ss past an hour), never negative. */
export function clock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const ss = String(total % 60).padStart(2, '0');
  return h > 0 ? `${String(h)}:${String(m).padStart(2, '0')}:${ss}` : `${String(m)}:${ss}`;
}

export const statusLabels: Record<MediaNode['status'], string> = {
  active: 'Ativa',
  paused: 'Pausada',
  disabled: 'Desativada',
};

const fieldMessages: Record<string, string> = {
  name: 'Informe um nome único, de até 64 caracteres.',
  engine_url: 'Use http://endereço:porta, sem caminho (a API do servidor de vídeo pelo túnel).',
  agent_url: 'Use http://endereço:porta, sem caminho, ou deixe vazio.',
  snap_pump_url: 'Use http://endereço:porta, sem caminho, ou deixe vazio.',
  public_host: 'Só o nome ou o IP, sem http:// e sem porta.',
  rtsp_port: 'Porta entre 1 e 65535.',
  replay_port: 'Porta entre 0 e 65535 (0: sem reprodução ONVIF).',
  webrtc_port: 'Porta entre 0 e 65535.',
  capacity: 'Capacidade entre 1 e 100000.',
  status: 'Situação inválida.',
};

/** The numeric fields of the node form: each must be a whole number. */
export const numericFields = ['rtsp_port', 'replay_port', 'webrtc_port', 'capacity'] as const;

/**
 * Checks the numeric fields before sending: anything but digits (letters, a
 * decimal, empty) would reach the API as null or fail as a malformed body.
 */
export function numericProblems(
  values: Record<(typeof numericFields)[number], string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const k of numericFields) {
    if (!/^\d+$/.test(values[k].trim())) out[k] = fieldMessages[k] ?? 'Valor inválido.';
  }
  return out;
}

export interface MediaNodeProblem {
  fields: Record<string, string>;
  form: string | null;
}

export const noMediaNodeProblem: MediaNodeProblem = { fields: {}, form: null };

/** Maps a media node API failure onto pt-BR messages. */
export function toMediaNodeProblem(err: unknown): MediaNodeProblem {
  if (err instanceof ApiError) {
    if (err.status === 422) {
      const fields: Record<string, string> = {};
      for (const [k, code] of Object.entries(err.fields)) {
        if (k === 'node_id' && code === 'inactive')
          return {
            fields: {},
            form: 'Só uma máquina ativa recebe equipamentos. Reative a máquina de destino ou escolha outra.',
          };
        if (k === 'device_id')
          return {
            fields: {},
            form: 'Esse equipamento não existe mais ou é um canal de gravador (canais acompanham o gravador).',
          };
        if (k === 'name' && code === 'taken') fields.name = 'Já existe uma máquina com esse nome.';
        else fields[k] = fieldMessages[k] ?? 'Valor inválido.';
      }
      return { fields, form: Object.keys(fields).length ? null : 'Confira os dados informados.' };
    }
    if (err.status === 409 && err.code === 'move_conflict')
      return {
        fields: {},
        form: 'O equipamento mudou enquanto era movido. Atualize a lista e tente de novo.',
      };
    if (err.status === 409 && err.code === 'rebalance_changed')
      return {
        fields: {},
        form: 'A distribuição mudou desde a prévia. Confira a nova prévia e confirme de novo.',
      };
    if (err.status === 409 && err.code === 'rebalance_busy')
      return {
        fields: {},
        form: 'Uma troca automática ou outro rebalanceamento está em andamento. Tente de novo em instantes.',
      };
    if (err.status === 409 && err.code === 'no_media_node')
      return {
        fields: {},
        form: 'Nenhuma máquina de vídeo pode receber equipamentos agora: todas estão fora do ar, pausadas ou com 70% da capacidade ocupada. Reative uma máquina, aumente a capacidade ou cadastre outra.',
      };
    if (err.status === 403) return { fields: {}, form: 'Você não tem permissão para esta ação.' };
    if (err.status === 404) return { fields: {}, form: 'Esta máquina não existe mais.' };
  }
  if (err instanceof NetworkError)
    return { fields: {}, form: 'Não foi possível contatar o servidor. Tente novamente.' };
  return { fields: {}, form: 'Ocorreu um erro inesperado. Tente novamente.' };
}

/** What a node's agent measured (ADR-051); rates per second over its last sample. */
export interface NodeSystemStats {
  cpu_percent: number;
  cpus: number;
  load1: number;
  load5: number;
  load15: number;
  memory_used_bytes: number;
  memory_total_bytes: number;
  /** Bytes in the spool, waiting to be stored. */
  spool_bytes: number;
  spool_files: number;
  /** The agent's limit (MEDIA_AGENT_SPOOL_WARN_GB). */
  spool_warn_bytes: number;
  /** The spool's filesystem. */
  disk_free_bytes: number;
  disk_total_bytes: number;
  /** Loopback, tunnels and bridges left out. */
  network_in_bytes_per_second: number;
  network_out_bytes_per_second: number;
  uptime_seconds: number;
  /** What an agent 0.56-0.63 still holds in held/ (ADR-052/053); null or absent from an agent before 0.56.0. */
  hold?: NodeHoldStats | null;
  /** How the recorded segments leave the node; null or absent from an agent before 0.60.0. */
  upload?: NodeUploadStats | null;
  /** What the node keeps while the bucket is down; null or absent from an agent before 0.65.0. */
  local?: NodeLocalStats | null;
}

/** What the node keeps on its disk while the bucket is down (ADR-054). */
export interface NodeLocalStats {
  files: number;
  bytes: number;
  /** Evidence (event, the seconds before, DVR copy): never removed. */
  protected_files: number;
  protected_bytes: number;
  /** When the oldest of them was kept; null or absent when none. */
  since?: string | null;
  /** Continuous segments removed for lack of disk since the agent started. */
  dropped_files: number;
  dropped_bytes: number;
  /** The disk stays below its limit with only evidence left. */
  spool_evidence_only: boolean;
  min_free_percent: number;
}

/** Why the node's last upload failed (0.60.0). */
export type UploadErrorCode =
  'bucket_error' | 'central_unreachable' | 'central_refused' | 'timeout' | 'internal';

/** How the recorded segments leave the node (0.60.0); times are the agent's clock. */
export interface NodeUploadStats {
  /** The last segment uploaded and registered; null when none since the agent started. */
  last_ok: string | null;
  /** First failure of the current streak; null when the last attempt worked. */
  failing_since: string | null;
  /** How long the current streak has lasted, on the agent's own clock; null when not failing. */
  failing_seconds: number | null;
  /** The latest failure, kept after a success. */
  last_error: { code: UploadErrorCode; message: string } | null;
  /** Age of the oldest segment waiting in the spool (held and rejected left out). */
  oldest_pending_seconds: number;
}

/** What the node still holds in held/ from before 0.64.0 (ADR-053): drained, nothing new is held. */
export interface NodeHoldStats {
  /** Always false since 0.64.0. */
  enabled: boolean;
  /** Always false since 0.64.0. */
  suspended: boolean;
  min_free_percent: number;
  held_files: number;
  held_bytes: number;
  /** Discarded without an event since `since` (the agent's start). */
  discarded_files: number;
  discarded_bytes: number;
  since: string;
}

/** What a node's video server reported. */
export interface NodeVideoStats {
  streams: number;
  viewers: number;
  recordings: number;
  bytes_per_second: number;
}

export interface NodeStatsPoint {
  at: string;
  /** null without an agent, or with one older than 0.53.0. */
  system: NodeSystemStats | null;
  video: NodeVideoStats | null;
}

export type NodeWarningCode =
  | 'cpu_high'
  | 'memory_high'
  | 'spool_high'
  | 'network_overhead'
  | 'over_capacity'
  | 'upload_failing'
  | 'spool_full_evidence';

export interface NodeWarning {
  code: NodeWarningCode;
  /** Percent, bytes for spool_high, minutes for upload_failing. */
  value: number;
  limit: number;
  /** upload_failing: the last upload error code, when the agent gave one. */
  detail?: string;
}

/** Why the recordings are not leaving the node, in pt-BR, by upload error code. */
export const uploadReasons: Record<UploadErrorCode, string> = {
  bucket_error: 'o bucket recusou o envio (cheio ou fora do ar?)',
  central_unreachable: 'a central não respondeu',
  central_refused: 'a central recusou o registro',
  timeout: 'tempo esgotado',
  internal: 'erro interno',
};

/** The reason for an upload error code; unknown or absent reads as not given. */
export function uploadReason(code: string | undefined): string {
  return code && Object.hasOwn(uploadReasons, code)
    ? uploadReasons[code as UploadErrorCode]
    : 'motivo não informado';
}

/** The upload fails on the bucket's side (ADR-054): B2 down or refusing. */
const bucketDown = (code: string | undefined) => code === 'bucket_error' || code === 'timeout';

/**
 * The node's recordings are not leaving it (warning upload_failing in the
 * list). An agent reports while it reaches the central, so in the list this
 * reads as the bucket being down.
 */
export const uploadFailing = (n: Pick<MediaNode, 'warnings'>) =>
  n.warnings?.includes('upload_failing') ?? false;

/** One of a node's heaviest ingests now; names are empty once the device is deleted. */
export interface NodeHeavyStream {
  device_id: string;
  device_name: string;
  /** The recorder of a channel, '' for a standalone camera. */
  parent_name: string;
  tenant_name: string;
  site_name: string;
  /** '' without a video track. */
  codec: string;
  width: number;
  height: number;
  fps: number;
  kbps: number;
  viewers: number;
  recording: boolean;
  /** kbps above heavy_kbps. */
  heavy: boolean;
}

/** Live sessions the central served for a node over the last hour (0.62.0). */
export interface NodeLiveStats {
  webrtc_sessions: number;
  hls_sessions: number;
  /** HLS bytes (playlist and segments) that went through the central. */
  hls_relayed_bytes: number;
  /** The window start: an hour ago, or when the API started if later. */
  since: string;
}

/** The node page's live line: WebRTC against HLS, and what HLS cost the central. */
export function liveSummary(l: NodeLiveStats): string {
  const total = l.webrtc_sessions + l.hls_sessions;
  if (total === 0) return 'Ao vivo: sem sessões na última hora';
  const pct = Math.round((l.hls_sessions * 100) / total);
  return `Ao vivo na última hora: ${String(l.webrtc_sessions)} por WebRTC · ${String(l.hls_sessions)} por HLS (${String(pct)}% HLS) · ${formatBytes(l.hls_relayed_bytes)} do HLS passaram pela central`;
}

/** GET /api/v1/platform/media-nodes/{id}/stats: the node's last hour (ADR-051). */
export interface NodeStats {
  node: MediaNode;
  now: string;
  interval_seconds: number;
  /** The node has an agent: without one, `system` is always null. */
  agent: boolean;
  engine_version: string;
  /** Cameras assigned (recorders count their channels). */
  cameras: number;
  /** Oldest first, at most one hour. */
  points: NodeStatsPoint[];
  warnings: NodeWarning[];
  /** The 10 heaviest ingests at the last collection, heaviest first. */
  streams: NodeHeavyStream[];
  /** MEDIA_HEAVY_STREAM_KBPS: a stream above it is flagged. */
  heavy_kbps: number;
  live: NodeLiveStats;
}

/** "DVR Galpão › Canal 1"; "Câmera excluída" once the device is gone. */
export function streamCamera(s: NodeHeavyStream): string {
  if (!s.device_name) return 'Câmera excluída';
  return s.parent_name ? `${s.parent_name} › ${s.device_name}` : s.device_name;
}

/** "H264 · 1920×1080 · 25 fps", like the system page; "aguardando vídeo" without a track. */
export function streamVideo(s: NodeHeavyStream): string {
  if (!s.codec) return 'aguardando vídeo';
  return `${s.codec} · ${String(s.width)}×${String(s.height)}${
    s.fps ? ` · ${String(Math.round(s.fps))} fps` : ''
  }`;
}

/**
 * The machine's incoming network beyond the video it received (bytes per
 * second, never negative) and its share of the input; null without both.
 */
export function networkOverhead(
  p: NodeStatsPoint,
): { bytes: number; percent: number | null } | null {
  if (!p.system || !p.video) return null;
  const input = p.system.network_in_bytes_per_second;
  const bytes = Math.max(input - p.video.bytes_per_second, 0);
  return { bytes, percent: input > 0 ? (bytes / input) * 100 : null };
}

/** "1 equipamento", "2 equipamentos". */
export const plural = (n: number, one: string, many: string) =>
  `${String(n)} ${n === 1 ? one : many}`;

/** One hour of points, one every 15 s. */
export const statsSlots = 240;

export const nodeStatsKey = (id: string) => ['platform', 'media-nodes', id, 'stats'] as const;

export const getNodeStats = (req: AuthedRequest, id: string) =>
  req<NodeStats>('GET', `${path}/${encodeURIComponent(id)}/stats`);

const oneDecimal = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });

/** A percentage in pt-BR: "85,5%". */
export const formatPercent = (v: number) => `${oneDecimal.format(v)}%`;

/** A warning of the node page, in pt-BR, saying what to act on. */
export function warningText(w: NodeWarning): string {
  switch (w.code) {
    case 'cpu_high':
      return `CPU em ${formatPercent(w.value)} em média nos últimos 5 minutos (limite ${formatPercent(w.limit)}).`;
    case 'memory_high':
      return `Memória em ${formatPercent(w.value)} (limite ${formatPercent(w.limit)}).`;
    case 'spool_high':
      return `Pasta temporária com ${formatBytes(w.value)}, acima do limite de ${formatBytes(w.limit)}: os trechos gravados não estão saindo da máquina.`;
    case 'network_overhead':
      return `Entrada além do vídeo em ${formatPercent(w.value)} nos últimos 5 min (limite ${formatPercent(w.limit)}): rede perdendo pacotes ou tráfego desconhecido.`;
    case 'over_capacity':
      return `Ocupação em ${formatPercent(w.value)}: acima da capacidade. Aumente a capacidade ou use Rebalancear.`;
    case 'upload_failing':
      return bucketDown(w.detail)
        ? `B2 indisponível há ${String(Math.floor(w.value))} min: ${uploadReason(w.detail)}. Os trechos ficam guardados no disco da máquina, aparecem na reprodução e sobem sozinhos quando ele voltar.`
        : `Gravações sem sair da máquina há ${String(Math.floor(w.value))} min: ${uploadReason(w.detail)}. Os trechos ficam guardados no disco da máquina e sobem sozinhos quando o problema passar.`;
    case 'spool_full_evidence':
      return `Disco da máquina com ${formatPercent(w.value)} livre, abaixo do limite de ${formatPercent(w.limit)}, só com evidência guardada: nada mais é apagado e a gravação para quando o disco acabar. Libere espaço ou restabeleça o B2.`;
  }
}

/**
 * The drain line of the spool card (ADR-053): what an agent of 0.56-0.63
 * still holds in held/ until the central decides it; null when nothing is
 * left, or without the agent's numbers.
 */
export function heldLine(h: NodeHoldStats | null | undefined): string | null {
  if (!h || h.held_files <= 0) return null;
  return `Segurando ${plural(h.held_files, 'trecho', 'trechos')} (${formatBytes(h.held_bytes)}) de versões anteriores, até esvaziar`;
}

/**
 * "HH:MM" for an instant of today in timeZone, "DD/MM HH:MM" for another day:
 * the "since" of what the node keeps can be days old.
 */
export function sinceLabel(iso: string, now: Date, timeZone?: string): string {
  const full = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeZone });
  const at = new Date(iso);
  const clock = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone,
  }).format(at);
  if (full.format(at) === full.format(now)) return clock;
  const day = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', timeZone });
  return `${day.format(at)} ${clock}`;
}

/**
 * The lines of the spool card about what the node keeps while the bucket is
 * down (ADR-054); empty when nothing, or without the agent's numbers.
 */
export function localLines(
  l: NodeLocalStats | null | undefined,
  clockOf: (iso: string) => string,
): string[] {
  if (!l) return [];
  const out: string[] = [];
  if (l.files > 0) {
    const since = l.since ? `, desde ${clockOf(l.since)}` : '';
    const evidence =
      l.protected_files > 0
        ? ` · ${plural(l.protected_files, 'é evidência', 'são evidência')}`
        : '';
    out.push(
      `Guardado na máquina (B2 indisponível): ${plural(l.files, 'trecho', 'trechos')}, ${formatBytes(l.bytes)}${since}${evidence}`,
    );
  }
  if (l.dropped_files > 0) {
    out.push(
      `Apagados por falta de espaço: ${plural(l.dropped_files, 'trecho contínuo', 'trechos contínuos')} (${formatBytes(l.dropped_bytes)})`,
    );
  }
  return out;
}

/** The node's disk is full of evidence (warning spool_full_evidence in the list). */
export const spoolFullEvidence = (n: Pick<MediaNode, 'warnings'>) =>
  n.warnings?.includes('spool_full_evidence') ?? false;
